import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid2 as Grid,
  Card,
  IconButton,
  CircularProgress,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardDisplay from "../noc/CreditCardDisplay";
import axios from "axios";
import validator from "validator";
import { handlePhoneInputChange as formatPhone } from "../../functions/permitFormHelpers";
import { floridaCounties } from "../../data/counties";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const NotaryUserPaymentModal = ({
  open,
  onClose,
  notaryUserData,
  currentUser,
  currentSession,
  onPaymentSuccess,
  fromSubusersPage = false,
}) => {
  const [paymentCards, setPaymentCards] = useState([]);
  const [cardActiveStep, setCardActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");

  // Editable notary contact/location fields
  const [notaryState, setNotaryState] = useState("");
  const [notaryCounty, setNotaryCounty] = useState("");
  const [notaryEmail, setNotaryEmail] = useState("");
  const [notaryPhone, setNotaryPhone] = useState("");
  const [commissionExpiry, setCommissionExpiry] = useState(null);

  // Field validation errors
  const [fieldErrors, setFieldErrors] = useState({
    state: "",
    county: "",
    email: "",
    phone: "",
  });

  // Notary search states
  const [searchingNotary, setSearchingNotary] = useState(false);
  const [foundNotary, setFoundNotary] = useState(null);
  const [notarySearched, setNotarySearched] = useState(false);

  const isBranchUser = Boolean(
    currentUser?.user?.enterprise?.is_branch || currentUser?.user?.is_branch,
  );

  const configuredNotaryPrice = currentUser?.service_tiers?.notary_user?.price;
  const numericNotaryPrice = Number(configuredNotaryPrice ?? NaN);
  const isFreeNotary =
    configuredNotaryPrice !== undefined &&
    configuredNotaryPrice !== null &&
    configuredNotaryPrice !== "" &&
    !Number.isNaN(numericNotaryPrice) &&
    numericNotaryPrice === 0;
  const hidePaymentUI = isBranchUser && isFreeNotary;

  const searchNotaryByName = useCallback(
    async (firstName, lastName, commissionNumber) => {
      setSearchingNotary(true);
      setErrorText("");

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/data/search-notary-by-name`,
          {
            first_name: firstName,
            last_name: lastName,
            commission_number: commissionNumber,
            currentSession: currentSession,
          },
          { withCredentials: true },
        );

        if (response.data.success) {
          setFoundNotary(response.data.notary);
          setNotarySearched(true);
          // Don't show error for expired commission - user can edit it
        } else {
          setFoundNotary(null);
          setNotarySearched(true);
          setErrorText(
            "Notary not found with the provided name and commission number.",
          );
        }
      } catch (error) {
        setFoundNotary(null);
        setNotarySearched(true);
        setErrorText(
          error.response?.data?.message ||
            "Notary not found with the provided name and commission number.",
        );
      } finally {
        setSearchingNotary(false);
      }
    },
    [currentSession],
  );

  useEffect(() => {
    if (open && currentUser?.payment_cards?.length > 0) {
      const cards = currentUser.payment_cards.map((card) => {
        const lastTwo = String(card.exp_year).slice(-2);
        const first = card.exp_month;

        return {
          id: card.id,
          cardNum: "**** **** **** " + card.last4,
          cardName: card.name ? card.name : card.card_holder,
          expires:
            (first < 10 ? "0" + String(first) : String(first)) + "/" + lastTwo,
          default: card.default,
        };
      });

      setPaymentCards(cards);
    }
  }, [open, currentUser?.payment_cards]);

  useEffect(() => {
    // Reset form when modal opens/closes
    if (open) {
      setErrorText("");
      setFoundNotary(null);
      setNotarySearched(false);

      // Reset editable fields
      setNotaryState("");
      setNotaryCounty("");
      setNotaryEmail("");
      setNotaryPhone("");
      setCommissionExpiry(null);
      setFieldErrors({
        state: "",
        county: "",
        email: "",
        phone: "",
      });

      // Search for notary automatically for both new users and conversions
      if (notaryUserData?.first && notaryUserData?.last) {
        searchNotaryByName(
          notaryUserData.first,
          notaryUserData.last,
          notaryUserData.commission_number || "",
        );
      } else if (open && notaryUserData) {
        // If names are missing, set error state
        setNotarySearched(true);
        setFoundNotary(null);
        setErrorText("User name information is missing. Please try again.");
      }
    }
  }, [open, notaryUserData, searchNotaryByName]);

  // Helper function to check if commission expiry is in the past
  const isCommissionExpired = useCallback((expiryDate) => {
    if (!expiryDate) return false;

    // If it's a dayjs object
    if (dayjs.isDayjs(expiryDate)) {
      return expiryDate.isBefore(dayjs(), "day");
    }

    // If it's a string in MM/DD/YYYY format
    if (typeof expiryDate === "string" && expiryDate.includes("/")) {
      const [month, day, year] = expiryDate.split("/");
      const date = dayjs(`${year}-${month}-${day}`);
      return date.isBefore(dayjs(), "day");
    }

    return false;
  }, []);

  // Helper function to parse commission expiry string to dayjs
  const parseCommissionExpiry = useCallback((expiryString) => {
    if (!expiryString) return null;

    // Parse MM/DD/YYYY format
    if (typeof expiryString === "string" && expiryString.includes("/")) {
      const [month, day, year] = expiryString.split("/");
      return dayjs(`${year}-${month}-${day}`);
    }

    return null;
  }, []);

  // When a notary is found, prefill editable fields
  useEffect(() => {
    if (foundNotary) {
      setNotaryState(foundNotary.state || "");
      setNotaryCounty(foundNotary.county || "");
      setNotaryEmail(foundNotary.email || "");
      setNotaryPhone(foundNotary.phone || "");

      // Parse commission expiry if available
      if (foundNotary.commissionExpiry) {
        const parsedDate = parseCommissionExpiry(foundNotary.commissionExpiry);
        setCommissionExpiry(parsedDate);
      } else {
        setCommissionExpiry(null);
      }
    }
  }, [foundNotary, parseCommissionExpiry]);

  const validateFields = () => {
    const errors = {
      state: "",
      county: "",
      email: "",
      phone: "",
    };
    let isValid = true;

    // Validate State
    if (!notaryState || notaryState.trim() === "") {
      errors.state = "State is required";
      isValid = false;
    }

    // Validate County
    if (!notaryCounty || notaryCounty.trim() === "") {
      errors.county = "County is required";
      isValid = false;
    }

    // Validate Email
    if (!notaryEmail || notaryEmail.trim() === "") {
      errors.email = "Email is required";
      isValid = false;
    } else if (!validator.isEmail(notaryEmail)) {
      errors.email = "Please enter a valid email address";
      isValid = false;
    } else {
      // Check if email matches current logged in user's email
      // If fromSubusersPage, compare with notaryUserData email instead
      const emailToCompare = fromSubusersPage
        ? notaryUserData?.email || ""
        : currentUser?.user?.email || "";

      if (notaryEmail.toLowerCase() === emailToCompare.toLowerCase()) {
        errors.email =
          "Email cannot be the same as your logged in email address";
        isValid = false;
      }
    }

    // Validate Phone
    if (!notaryPhone || notaryPhone.trim() === "") {
      errors.phone = "Phone number is required";
      isValid = false;
    } else if (!validator.isMobilePhone(notaryPhone)) {
      errors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handlePayment = async () => {
    if (!hidePaymentUI && paymentCards.length === 0) {
      setErrorText(
        "No payment cards available. Please add a payment card first.",
      );
      return;
    }

    // Validate all fields before proceeding
    if (!validateFields()) {
      setErrorText("Please fix the errors in the form before proceeding.");
      return;
    }

    setLoading(true);
    setErrorText("");

    try {
      // First, make the Stripe payment for notary user
      const paymentResp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/pay-notary-user`,
        {
          paymentCard: hidePaymentUI ? "" : paymentCards[cardActiveStep].id,
          currentSession: currentSession,
          notaryUserData: notaryUserData,
          notaryState: notaryState,
          notaryCounty: notaryCounty,
          notaryEmail: notaryEmail,
          notaryPhone: notaryPhone,
          is_main_user: notaryUserData?.isMainUser,
        },
        { withCredentials: true },
      );

      if (paymentResp?.data?.success) {
        // Format commission expiry: use edited date if available, otherwise use foundNotary date
        let commissionExpiryToSend = "";
        if (commissionExpiry && dayjs.isDayjs(commissionExpiry)) {
          // Format as MM/DD/YYYY
          commissionExpiryToSend = commissionExpiry.format("MM/DD/YYYY");
        } else if (foundNotary?.commissionExpiry) {
          commissionExpiryToSend = foundNotary.commissionExpiry;
        }

        if (notaryUserData?.isConversion) {
          let payload = {
            subscription_id: paymentResp.data.subscription_id,
            price_id: paymentResp.data.price_id,
            currentSession: currentSession,
            // Only send important textfield values - backend will fetch rest from notaries collection
            commission_number: foundNotary?.commissionNumber || "",
            commission_expiry: commissionExpiryToSend,
            state: notaryState,
            county: notaryCounty,
            email: notaryEmail,
            phone: notaryPhone,
            fromSubusersPage: fromSubusersPage,
            notaryUserData: notaryUserData,
          };

          if (notaryUserData?.isMainUser) {
            payload.is_main_user = true;
          }
          // Note: subuserId is no longer needed - backend gets it from notaryUserData.email

          // Convert existing subuser to notary with commission details
          const conversionResp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/subuser/convert-to-notary`,
            payload,
            { withCredentials: true },
          );

          if (conversionResp.data.success) {
            onPaymentSuccess(conversionResp.data.message);
            onClose();
          } else {
            const errorMessage = conversionResp.data.message || "";
            // Check if error is email-related
            if (errorMessage.toLowerCase().includes("email")) {
              setFieldErrors({
                ...fieldErrors,
                email: errorMessage,
              });
            } else {
              setErrorText(errorMessage);
            }
          }
        } else {
          // Create new subuser with notary data
          // Backend will fetch notary details from notaries collection using commission_number
          const notaryUserPayload = {
            is_notary: true,
            subscription_id: paymentResp.data.subscription_id,
            price_id: paymentResp.data.price_id,
            // Only send essential textfield values - backend will fetch rest from notaries collection
            commission_number: foundNotary?.commissionNumber || "",
            commission_expiry: commissionExpiryToSend,
            state: notaryState,
            county: notaryCounty,
            email: notaryEmail,
            phone: notaryPhone,
          };

          const subuserResp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/subuser/add-subuser`,
            {
              values: {
                ...notaryUserData,
                notary_user: notaryUserPayload,
              },
              currentSession: currentSession,
            },
            { withCredentials: true },
          );

          if (subuserResp.data.success) {
            onPaymentSuccess(subuserResp.data.message);
            onClose();
          } else {
            const errorMessage = subuserResp.data.message || "";
            // Check if error is email-related
            if (errorMessage.toLowerCase().includes("email")) {
              setFieldErrors({
                ...fieldErrors,
                email: errorMessage,
              });
            } else {
              setErrorText(errorMessage);
            }
          }
        }
      } else {
        const errorMessage = paymentResp.data.message || "";
        // Check if error is email-related
        if (errorMessage.toLowerCase().includes("email")) {
          setFieldErrors({
            ...fieldErrors,
            email: errorMessage,
          });
        } else {
          setErrorText(errorMessage);
        }
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Payment failed. Please try again.";
      // Check if error is email-related
      if (errorMessage.toLowerCase().includes("email")) {
        setFieldErrors({
          ...fieldErrors,
          email: errorMessage,
        });
      } else {
        setErrorText(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const getNotaryPrice = () => {
    const configuredPrice = currentUser?.service_tiers?.notary_user?.price;
    if (
      configuredPrice !== undefined &&
      configuredPrice !== null &&
      configuredPrice !== ""
    ) {
      return configuredPrice;
    }

    return currentUser?.tier?.title === "Premium" ? "149.00" : "99.00";
  };

  const getSubmitButtonLabel = () => {
    if (hidePaymentUI) {
      return notaryUserData?.isConversion || notaryUserData?.isMainUser
        ? "Continue"
        : "Add Notary User";
    }

    return `Pay $${getNotaryPrice()}/year`;
  };

  const getNotaryLimit = () => {
    // Admin users get 10 notary users
    if (currentUser?.user?.role === "admin") {
      return "10";
    }

    const configuredLimit = currentUser?.service_tiers?.notary_user?.limit;
    if (
      configuredLimit !== undefined &&
      configuredLimit !== null &&
      configuredLimit !== ""
    ) {
      return configuredLimit;
    }

    return currentUser?.tier?.title === "Premium" ? "2" : "Unlimited";
  };

  const getNotaryTypeLabel = (notaryType) => {
    switch (notaryType) {
      case 0:
        return "IPEN Only";
      case 1:
        return "IPEN & RON";
      case 2:
        return "IPEN & RON";
      default:
        return "Unknown";
    }
  };

  return (
    <Modal open={open} sx={{ overflow: "auto" }}>
      <Box
        sx={{
          position: "relative",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90%",
          maxWidth: "50rem",
          bgcolor: "transparent",
          animation: "fadeIn 0.5s ease-in-out",
        }}
      >
        <Card
          sx={{
            maxWidth: "55rem",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            mx: "auto",
            my: "auto",
            border: "3px solid #d3d3d3",
            borderRadius: "18px",
            boxShadow: "2px 0px 10px 0px #d3d3d3",
            p: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Sticky Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              bgcolor: "background.paper",
              borderBottom: "1px solid #e0e0e0",
              px: 2,
              pt: 2,
              pb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
                alignItems: "center",
              }}
            >
              <Typography variant="h5" color="primary">
                {notaryUserData?.isConversion && !notaryUserData?.isMainUser
                  ? "Convert to Notary User"
                  : notaryUserData?.isMainUser
                    ? "Setup Notary User"
                    : "Notary User Payment"}
              </Typography>
              <IconButton onClick={onClose} disabled={loading}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="body1" gutterBottom>
              {hidePaymentUI
                ? "No additional charge for branch accounts."
                : notaryUserData?.isConversion && !notaryUserData?.isMainUser
                  ? `You are converting ${notaryUserData.first} ${notaryUserData.last} to a Notary User. This will incur an additional charge of $${getNotaryPrice()}/year.`
                  : notaryUserData?.isMainUser
                    ? `You are setting up ${notaryUserData.first} ${notaryUserData.last} as a Notary User. This will incur an additional charge of $${getNotaryPrice()}/year.`
                    : `You are adding a Notary User to your account. This will incur an additional charge of $${getNotaryPrice()}/year.`}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Notary User Limit: {getNotaryLimit()}
            </Typography>
          </Box>

          {/* Scrollable Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 2,
              py: 1.5,
            }}
          >
            <Grid container spacing={1.5}>
              {/* Combined User Details and Notary Search Results */}
              <Grid size={{ xs: 12 }}>
                {searchingNotary ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <CircularProgress size={20} />
                    <Typography variant="body2">
                      Searching for notary...
                    </Typography>
                  </Box>
                ) : notarySearched && !foundNotary ? (
                  <Box
                    sx={{
                      p: 2,
                      border: "2px solid #ff9800",
                      borderRadius: 2,
                      bgcolor: "#fff3e0",
                    }}
                  >
                    <Typography variant="h6" color="error" gutterBottom>
                      ⚠️ Notary Not Found
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {errorText ||
                        `No notary record found for ${notaryUserData?.first} ${notaryUserData?.last}. Please verify the name and commission number, or try a different user.`}
                    </Typography>
                    {!notaryUserData?.isMainUser && (
                      <>
                        <Typography variant="body2">
                          <strong>Email:</strong>
                          {notaryUserData?.isMainUser
                            ? ` ${currentUser?.user?.email}`
                            : ` ${notaryUserData?.email}`}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Role:</strong>
                          {notaryUserData?.isMainUser
                            ? ` ${currentUser?.user?.role}`
                            : ` ${notaryUserData?.role}`}
                        </Typography>
                      </>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      border: `2px solid ${
                        foundNotary?.is_expired
                          ? "#f44336"
                          : foundNotary
                            ? "#4caf50"
                            : "#2196f3"
                      }`,
                      borderRadius: 2,
                      bgcolor: foundNotary?.is_expired
                        ? "#ffebee"
                        : foundNotary
                          ? "#e8f5e9"
                          : "#e3f2fd",
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      {foundNotary?.is_expired
                        ? "⚠️ Notary Found (Commission Expired)"
                        : foundNotary
                          ? "✓ User & Notary Details"
                          : "User Details"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Name:</strong>
                      {notaryUserData?.isMainUser
                        ? ` ${currentUser?.user?.personal?.[0]?.firstName} ${currentUser?.user?.personal?.[0]?.lastName}`
                        : ` ${notaryUserData?.first} ${notaryUserData?.last}`}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong>
                      {notaryUserData?.isMainUser
                        ? ` ${currentUser?.user?.email}`
                        : ` ${notaryUserData?.email}`}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Role:</strong>
                      {notaryUserData?.isMainUser
                        ? ` ${currentUser?.user?.role}`
                        : ` ${notaryUserData?.role}`}
                    </Typography>

                    {foundNotary && (
                      <>
                        <Box
                          sx={{
                            borderTop: "1px solid",
                            borderColor: foundNotary.is_expired
                              ? "#f44336"
                              : "#4caf50",
                            mt: 2,
                            pt: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: "bold" }}
                          >
                            Notary Commission Details:
                          </Typography>
                          <Typography variant="body2">
                            <strong>Commission Number:</strong>{" "}
                            {foundNotary.commissionNumber}
                          </Typography>

                          <Typography variant="body2">
                            <strong>Notary Type:</strong>{" "}
                            {getNotaryTypeLabel(foundNotary.notaryType)}
                          </Typography>
                          {foundNotary.middleName && (
                            <Typography variant="body2">
                              <strong>Middle Name:</strong>{" "}
                              {foundNotary.middleName}
                            </Typography>
                          )}
                          {foundNotary.address && (
                            <Typography variant="body2">
                              <strong>Address:</strong> {foundNotary.address}
                            </Typography>
                          )}
                          {foundNotary.city && (
                            <Typography variant="body2">
                              <strong>City:</strong> {foundNotary.city}
                            </Typography>
                          )}
                          {foundNotary.zip && (
                            <Typography variant="body2">
                              <strong>Zip:</strong> {foundNotary.zip}
                            </Typography>
                          )}

                          <Grid
                            container
                            spacing={1}
                            sx={{
                              mt: 1,
                              border: "2px solid #d3d3d3",
                              borderRadius: 1,
                              boxShadow: "2px 0px 10px 0px #d3d3d3",
                              backgroundColor: "white",
                              p: 1,
                            }}
                          >
                            <Grid size={{ xs: 12 }} sx={{ py: 1 }}>
                              <Typography
                                variant="h6"
                                align="center"
                                color="primary"
                              >
                                Required Fields
                              </Typography>
                            </Grid>
                            <Grid size={{ md: 4, sm: 12 }}>
                              <TextField
                                select
                                label="State"
                                value={notaryState}
                                onChange={(e) => {
                                  setNotaryState(e.target.value);
                                  if (fieldErrors.state) {
                                    setFieldErrors({
                                      ...fieldErrors,
                                      state: "",
                                    });
                                  }
                                }}
                                required
                                size="small"
                                fullWidth
                                error={!!fieldErrors.state}
                                helperText={fieldErrors.state}
                                slotProps={{
                                  select: {
                                    MenuProps: {
                                      PaperProps: {
                                        style: {
                                          maxHeight: 300,
                                          maxWidth: 300,
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                <MenuItem value="Florida">Florida</MenuItem>
                              </TextField>
                            </Grid>
                            <Grid size={{ md: 4, sm: 12 }}>
                              <TextField
                                select
                                label="County"
                                value={notaryCounty}
                                onChange={(e) => {
                                  setNotaryCounty(e.target.value);
                                  if (fieldErrors.county) {
                                    setFieldErrors({
                                      ...fieldErrors,
                                      county: "",
                                    });
                                  }
                                }}
                                required
                                size="small"
                                fullWidth
                                error={!!fieldErrors.county}
                                helperText={fieldErrors.county}
                                slotProps={{
                                  select: {
                                    MenuProps: {
                                      PaperProps: {
                                        style: {
                                          maxHeight: 300,
                                          maxWidth: 300,
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                {floridaCounties.map((county) => (
                                  <MenuItem key={county} value={county}>
                                    {county}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid size={{ md: 4, sm: 12 }}>
                              <TextField
                                label="Phone No."
                                value={notaryPhone}
                                onChange={(e) => {
                                  const formatted = formatPhone(e);
                                  setNotaryPhone(formatted);
                                  if (fieldErrors.phone) {
                                    setFieldErrors({
                                      ...fieldErrors,
                                      phone: "",
                                    });
                                  }
                                }}
                                required
                                size="small"
                                fullWidth
                                error={!!fieldErrors.phone}
                                helperText={fieldErrors.phone}
                              />
                            </Grid>
                            <Grid size={{ md: 6, sm: 12 }}>
                              <TextField
                                label="Email"
                                type="email"
                                value={notaryEmail.toLowerCase()}
                                onChange={(e) => {
                                  setNotaryEmail(e.target.value);
                                  if (fieldErrors.email) {
                                    setFieldErrors({
                                      ...fieldErrors,
                                      email: "",
                                    });
                                  }
                                }}
                                required
                                size="small"
                                fullWidth
                                error={!!fieldErrors.email}
                                helperText={fieldErrors.email}
                              />
                            </Grid>

                            <Grid size={{ sm: 12, md: 6 }}>
                              {/* Always show date picker for commission expiry */}
                              <Box>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <DatePicker
                                    label="Commission Expiry Date"
                                    value={commissionExpiry}
                                    onChange={(date) =>
                                      setCommissionExpiry(date)
                                    }
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        fullWidth: true,
                                        variant: "filled",
                                        error:
                                          isCommissionExpired(commissionExpiry),
                                        helperText: isCommissionExpired(
                                          commissionExpiry,
                                        )
                                          ? "Commission expiry must be in the future"
                                          : "",
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                textAlign="center"
                              >
                                Email must be different from your Permit
                                Rockstar email. You may use an email alias (for
                                example, name+alias@domain.com) for automated
                                emails. Ensure your email provider accepts
                                aliases, most do.
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Grid>

              {!hidePaymentUI && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 0 }}>
                    Select Payment Method:
                  </Typography>
                  {paymentCards.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        p: 1,
                        border: "2px dashed #ccc",
                        borderRadius: 2,
                      }}
                    >
                      <Typography color="error" gutterBottom>
                        No payment cards available
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                      >
                        Please add a payment card first before proceeding with
                        the notary user payment.
                      </Typography>
                    </Box>
                  ) : (
                    <CreditCardDisplay
                      cards={paymentCards}
                      activeStep={cardActiveStep}
                      setActiveStep={setCardActiveStep}
                      checkDefault={false}
                      loading={false}
                      setLoading={() => {}}
                    />
                  )}
                </Grid>
              )}

              {errorText && (
                <Grid size={{ xs: 12 }}>
                  <Typography color="error" textAlign="center">
                    {errorText}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Sticky Footer */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 10,
              bgcolor: "background.paper",
              borderTop: "1px solid #e0e0e0",
              px: 2,
              py: 2,
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePayment}
              disabled={
                loading ||
                (!hidePaymentUI && paymentCards.length === 0) ||
                !foundNotary ||
                isCommissionExpired(
                  commissionExpiry || foundNotary.commissionExpiry,
                ) ||
                !!fieldErrors.state ||
                !!fieldErrors.county ||
                !!fieldErrors.email ||
                !!fieldErrors.phone
              }
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? "Processing..." : getSubmitButtonLabel()}
            </Button>
          </Box>
        </Card>
      </Box>
    </Modal>
  );
};

export default NotaryUserPaymentModal;
